export const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:image/png;base64,")
      const base64String = reader.result.split(',')[1];
      resolve({
        name: file.name,
        content: base64String,
        media_type: file.type
      });
    };
    reader.onerror = (error) => reject(error);
  });
};

export const updateProgressSteps = (prev, jsonResponse) => {
  // tool_return events have no user-facing message and create noise
  // between keepalive events (they render as the literal "tool_return"
  // label and cause the next same-message keepalive to appear as a
  // duplicate instead of refreshing in place).
  if (jsonResponse.status === 'tool_return') {
    return prev;
  }

  const newStep = {
    status: jsonResponse.status,
    message: jsonResponse.message || "",
    timestamp: Date.now(),
    id: `${jsonResponse.status}-${Date.now()}-${Math.random()}`
  };

  // Sub-second duplicate guard — match on message only (ignoring status)
  // so events like agent_initializing and result_generating that carry the
  // same user-visible text (e.g. "Generating concise response...") don't
  // both appear as separate steps.
  if (newStep.message) {
    const isDuplicate = prev.some(step =>
      step.message === newStep.message &&
      (Date.now() - step.timestamp) < 1000
    );
    if (isDuplicate) {
      return prev;
    }
  }

  // If the last step shows the same message (regardless of status), refresh
  // it in place. This handles repeated keepalives even when a tool_return or
  // other silent event briefly displaced the last-step position.
  const lastStep = prev[prev.length - 1];
  if (
    lastStep &&
    lastStep.message === newStep.message &&
    newStep.message !== ""
  ) {
    return [...prev.slice(0, -1), newStep];
  }

  // Keep only the last 5 steps to avoid clutter.
  const updated = [...prev, newStep];
  return updated.slice(-5);
};

export const parseLine = (line) => {
  try {
    return JSON.parse(line);
  } catch (parseError) {
    console.error("Failed to parse JSON line:", line, parseError);
    return null;
  }
};

// Decide what a streaming status event means for the concise/expansive
// phase, independent of any I/O so it can be unit-tested in isolation.
//
// Returns one of:
//   'transition' — enter the expansive ("full") phase: snapshot the concise
//                  answer, switch streamingPhase to 'full'.
//   'reset'      — a new concise generation is starting; clear the streaming
//                  pane but stay in the preliminary phase.
//   'none'       — no phase-related action (e.g. progress/tool events, or a
//                  generation signal received after we are already 'full').
//
// `expansive_starting` is the authoritative boundary: the backend emits it
// exactly once when the expansive phase begins, regardless of whether
// research tools run.  `result_generating` is a secondary trigger for the
// single-pass path where the concise agent itself escalates (its message
// includes "expansive").  The phase only ever advances from 'preliminary',
// so a later signal is a no-op and never resets in-flight expansive content.
export const resolveStreamPhaseAction = (
  status,
  message,
  currentPhase,
  hasConciseContent,
) => {
  if (currentPhase !== "preliminary") return "none";
  if (status !== "result_generating" && status !== "expansive_starting") {
    return "none";
  }
  const isExpansive =
    status === "expansive_starting" ||
    Boolean(message && message.toLowerCase().includes("expansive"));
  return hasConciseContent || isExpansive ? "transition" : "reset";
};
